import {
  addDoc,
  collection,
  deleteDoc,
  getDocs,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore';
import { auth, db } from '../firebase/config';
import AIService from './AIService';
import type {
  CreateFeatureGraphicDTO,
  FeatureGraphicCropSettings,
  GeneratedFeatureGraphic,
  GeneratedFeatureGraphicAsset,
  GooglePlayFeatureGraphicPublishResponse,
  GooglePlayFeatureGraphicValidationResponse,
} from '../types/featureGraphic';
import { buildAppContextLines } from '../utils/appContextPrompt';

const FEATURE_GRAPHICS_COLLECTION = 'appFeatureGraphics';
const VALIDATE_GOOGLE_PLAY_FEATURE_GRAPHIC_ENDPOINT =
  process.env.REACT_APP_VALIDATE_GOOGLE_PLAY_FEATURE_GRAPHIC_URL ||
  'https://europe-west1-translatorx-5774e.cloudfunctions.net/validateGooglePlayFeatureGraphic';
const PUBLISH_GOOGLE_PLAY_FEATURE_GRAPHIC_ENDPOINT =
  process.env.REACT_APP_PUBLISH_GOOGLE_PLAY_FEATURE_GRAPHIC_URL ||
  'https://europe-west1-translatorx-5774e.cloudfunctions.net/publishGooglePlayFeatureGraphic';

class FeatureGraphicService {
  private generateId(): string {
    return `fg_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private toDate(timestamp: unknown): Date {
    if (timestamp instanceof Timestamp) return timestamp.toDate();
    if (typeof timestamp === 'string' || typeof timestamp === 'number') return new Date(timestamp);
    return new Date();
  }

  private async postAuthenticatedJson<T>(
    url: string,
    body: Record<string, unknown>
  ): Promise<T> {
    await auth.authStateReady();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('Authentication required. Please sign in.');
    }

    const idToken = await currentUser.getIdToken();
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify(body),
    });

    const rawText = await response.text();
    let responseData: T | { error?: string; message?: string } | null = null;
    try {
      responseData = rawText ? JSON.parse(rawText) : null;
    } catch {
      responseData = null;
    }

    if (!response.ok) {
      const detailedMessage =
        (responseData as any)?.error ||
        (responseData as any)?.message ||
        (rawText && rawText.trim().length > 0 ? rawText.trim() : null);
      const error = new Error(
        detailedMessage || `Request failed with status ${response.status}`
      ) as Error & { responseData?: unknown };
      error.responseData = responseData;
      throw error;
    }

    return responseData as T;
  }

  async getFeatureGraphicsByAppId(appId: string, userId: string): Promise<GeneratedFeatureGraphic[]> {
    const q = query(
      collection(db, FEATURE_GRAPHICS_COLLECTION),
      where('appId', '==', appId),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);

    return snapshot.docs.map((docItem) => {
      const data = docItem.data();
      return {
        featureGraphicId: data.featureGraphicId,
        appId: data.appId,
        userId: data.userId,
        baseImageUrl: data.baseImageUrl,
        sourceAssetId: data.sourceAssetId,
        sourceStoragePath: data.sourceStoragePath,
        prompt: data.prompt,
        style: data.style,
        mood: data.mood,
        colorScheme: data.colorScheme || {},
        metadata: {
          generatedAt: this.toDate(data.metadata?.generatedAt),
          aiModel: data.metadata?.aiModel || '',
          provider: data.metadata?.provider || 'gemini',
        },
      } as GeneratedFeatureGraphic;
    });
  }

  async generateFeatureGraphic(
    userId: string,
    request: CreateFeatureGraphicDTO
  ): Promise<GeneratedFeatureGraphic[]> {
    const prompt = this.buildPrompt(request);
    const aiResult = await AIService.generateImage(prompt, {
      appId: request.appId,
      assetCategory: 'feature-graphic',
      model: request.model,
      aspectRatio: '16:9',
      size: '1536x1024',
      n: 1,
    });

    const generated: GeneratedFeatureGraphic[] = [];
    for (const image of aiResult.images) {
      const featureGraphicId = this.generateId();
      const docData = {
        featureGraphicId,
        appId: request.appId,
        userId,
        baseImageUrl: image.downloadUrl,
        sourceAssetId: image.assetId,
        sourceStoragePath: image.storagePath,
        prompt,
        style: request.style,
        mood: request.mood,
        colorScheme: request.colorScheme,
        metadata: {
          generatedAt: serverTimestamp(),
          aiModel: aiResult.model,
          provider: aiResult.provider,
        },
      };

      await addDoc(collection(db, FEATURE_GRAPHICS_COLLECTION), docData);

      generated.push({
        ...docData,
        metadata: {
          generatedAt: new Date(),
          aiModel: aiResult.model,
          provider: aiResult.provider,
        },
      } as GeneratedFeatureGraphic);
    }
    return generated;
  }

  async createAndroidFeatureGraphicAsset(
    source: GeneratedFeatureGraphic,
    crop: FeatureGraphicCropSettings
  ): Promise<GeneratedFeatureGraphicAsset> {
    if (!source.sourceStoragePath?.trim()) {
      throw new Error('Selected asset does not have a valid source storage path.');
    }

    const generated = await AIService.generateFeatureGraphic({
      appId: source.appId,
      sourceStoragePath: source.sourceStoragePath,
      crop,
    });

    return {
      width: generated.width,
      height: generated.height,
      fileName: generated.fileName,
      base64: generated.base64,
      mimeType: generated.mimeType,
      dataUrl: `data:${generated.mimeType || 'image/png'};base64,${generated.base64}`,
    };
  }

  async saveGeneratedFeatureGraphicAsset(
    featureGraphicId: string,
    userId: string,
    asset: GeneratedFeatureGraphicAsset
  ): Promise<void> {
    const q = query(
      collection(db, FEATURE_GRAPHICS_COLLECTION),
      where('featureGraphicId', '==', featureGraphicId),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return;
    }

    await updateDoc(snapshot.docs[0].ref, {
      latestGeneratedAsset: {
        width: asset.width,
        height: asset.height,
        fileName: asset.fileName,
        mimeType: asset.mimeType,
        generatedAt: new Date().toISOString(),
      },
      updatedAt: serverTimestamp(),
    });
  }

  async downloadFeatureGraphic(asset: GeneratedFeatureGraphicAsset, appName: string): Promise<void> {
    const safeAppName = appName.replace(/[^a-zA-Z0-9-_]/g, '_');
    const link = document.createElement('a');
    link.href = asset.dataUrl;
    link.download = `${safeAppName}-android-feature-graphic.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  async deleteFeatureGraphic(featureGraphicId: string, userId: string): Promise<void> {
    const q = query(
      collection(db, FEATURE_GRAPHICS_COLLECTION),
      where('featureGraphicId', '==', featureGraphicId),
      where('userId', '==', userId)
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error('Feature graphic not found');
    }
    await deleteDoc(snapshot.docs[0].ref);
  }

  async validateGooglePlayFeatureGraphic(params: {
    appId: string;
    featureGraphicId: string;
  }): Promise<GooglePlayFeatureGraphicValidationResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayFeatureGraphicValidationResponse>(
        VALIDATE_GOOGLE_PLAY_FEATURE_GRAPHIC_ENDPOINT,
        {
          appId: params.appId,
          featureGraphicId: params.featureGraphicId,
        }
      );

    return (
      responseData || {
        success: false,
        appId: params.appId,
        featureGraphicId: params.featureGraphicId,
        packageName: '',
        locale: '',
        playLocale: '',
        status: 'error',
        current: { count: 0, images: [] },
        proposed: { featureGraphicUrl: '' },
        validationIssues: [],
      }
    );
  }

  async publishGooglePlayFeatureGraphic(params: {
    appId: string;
    featureGraphicId: string;
  }): Promise<GooglePlayFeatureGraphicPublishResponse> {
    const responseData =
      await this.postAuthenticatedJson<GooglePlayFeatureGraphicPublishResponse>(
        PUBLISH_GOOGLE_PLAY_FEATURE_GRAPHIC_ENDPOINT,
        {
          appId: params.appId,
          featureGraphicId: params.featureGraphicId,
        }
      );

    return (
      responseData || {
        success: false,
        appId: params.appId,
        featureGraphicId: params.featureGraphicId,
        packageName: '',
        locale: '',
        playLocale: '',
        commitStatus: 'not_committed',
      }
    );
  }

  private buildPrompt(request: CreateFeatureGraphicDTO): string {
    const colors = [
      request.colorScheme.primary?.trim() ? `primary ${request.colorScheme.primary}` : '',
      request.colorScheme.secondary?.trim() ? `secondary ${request.colorScheme.secondary}` : '',
      request.colorScheme.accent?.trim() ? `accent ${request.colorScheme.accent}` : '',
    ]
      .filter(Boolean)
      .join(', ');

    const lines = [
      `Generate an Android Play Store feature graphic concept for "${request.appName}".`,
      `Target asset: Android feature graphic (final output will be 1024x500).`,
      'Composition target: design as if the visible canvas is exactly 1024x500 (2.048:1).',
      'Positioning guide: keep headline/branding/CTA-safe content centered and fully inside this 1024x500 frame, not near edges.',
      'Safe-area guide: keep all critical elements within the inner 80% area, and keep any text-like elements within the center 60% area.',
      request.styleMode === 'ai'
        ? `AI style direction: ${request.aiStylePrompt || 'Use modern, premium feature-graphic art direction.'}`
        : `Style: ${request.style}. Mood: ${request.mood}.`,
      `Colors: ${colors || 'no fixed palette specified'}.`,
      ...buildAppContextLines({
        name: request.appName,
        description: request.appDescription,
        category: request.appCategory,
        platform: request.appPlatform,
        storeUrl: request.appStoreUrl,
        androidStoreUrl: request.androidStoreUrl,
        iosStoreUrl: request.iosStoreUrl,
        researchContext: request.researchContext,
      }),
      `Keywords: ${
        request.styleMode === 'ai' ? 'N/A' : request.keywords.join(', ') || 'N/A'
      }.`,
      `Avoid elements: ${
        request.styleMode === 'ai' ? 'N/A' : request.avoidElements?.join(', ') || 'N/A'
      }.`,
      request.styleMode === 'ai'
        ? `AI selected style/mood anchor: style=${request.aiSuggestedStyle || 'minimalist'}, mood=${request.aiSuggestedMood || 'professional'}.`
        : `Extra direction: ${request.customPrompt?.trim() || 'N/A'}.`,
      'Constraints: cinematic composition, strong focal point, app marketing visual style, no text overlay, no watermark, and accessibility-safe contrast across foreground/background elements.',
      'Accessibility contrast rule: maintain WCAG-like high contrast (target at least 4.5:1 for any readable UI/text-like elements) so key content is clearly distinguishable.',
      'Quality constraints: crisp details, clean edges, no blur haze, no compression artifacts, no distorted anatomy, no malformed icons/logos, and no broken UI components.',
      'Human-subject constraints: avoid unnatural poses, impossible limb angles, warped hands/fingers, stretched torsos, or floating/disconnected body parts.',
      'Composition constraints: avoid split-body framing and avoid subjects whose limbs are cropped at awkward joints near frame boundaries.',
      'Default art direction: prefer product-centric composition (app UI/device + symbolic elements) over full-body characters unless the prompt explicitly asks for people.',
      'Framing constraints: keep all key visual elements fully in-frame with generous safe margins; do not crop important content at edges.',
      'Text-safe constraints: if any text appears in-scene despite no-text guidance, keep text short, correctly spelled, fully visible, and centered inside the inner safe area.',
      'Layout-safe area: place core subject and any text-like elements within the center 60% of the canvas to survive downstream crops.',
    ];
    return lines.join('\n');
  }
}

export default new FeatureGraphicService();
